import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { Badge } from '../ui/badge';
import { History, Lock, Loader2 } from 'lucide-react';

const ESTADO_COLORS_MAP = {
  ABIERTO: '#3b82f6', CERRADO: '#10b981', EN_INVESTIGACION: '#f59e0b', ESPERANDO_COMPLIANCE: '#ef4444',
  EN_VIGILANCIA: '#06b6d4', PENDIENTE_APROBACION: '#a855f7', RESUELTO: '#10b981', ARCHIVADO: '#94a3b8',
};

// Timeline del historial de casos del cliente
const ClienteHistorial = ({ caseId, casoActual, navigate, score }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/cases/${caseId}/historial-cliente`);
        setData(res.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    };
    load();
  }, [caseId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={24} /></div>;
  if (!data || data.total === 0) return <div className="text-center py-12 text-slate-400">Sin historial</div>;

  const casos = data.casos || [];
  const totalCasos = casos.length;
  const bloqueados = casos.filter(c => c.cuenta_bloqueada).length;
  const scoreMax = Math.max(...casos.map(c => c.score_riesgo || 0));
  const totalAlertas = casos.reduce((s, c) => s + (c.total_alertas || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <History size={20} className="text-orange-600" />
        <h3 className="text-lg font-bold text-slate-800">Historial del Cliente</h3>
        <Badge className="bg-orange-100 text-orange-700">{totalCasos} casos</Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Total Casos</p>
          <p className="text-xl font-bold text-slate-800">{totalCasos}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">{score ? 'Score del cliente' : 'Score Máximo'}</p>
          <p className="text-xl font-bold" style={{ color: (score?.score ?? scoreMax) >= 80 ? '#ef4444' : '#f59e0b' }} data-testid="historial-score-cliente">
            {score ? `${score.score} · ${score.nivel}` : scoreMax}
          </p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Veces Bloqueado</p>
          <p className="text-xl font-bold text-red-600">{bloqueados}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-[10px] text-slate-400 uppercase">Total Alertas</p>
          <p className="text-xl font-bold text-amber-600">{totalAlertas}</p>
        </div>
      </div>

      {/* Timeline visual */}
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
        {casos.map((c, i) => {
          const isCurrent = c.id === caseId;
          const color = ESTADO_COLORS_MAP[c.estado] || '#94a3b8';
          return (
            <div key={c.id} className={`relative pl-10 pb-6 ${isCurrent ? '' : 'cursor-pointer hover:bg-slate-50 rounded-lg -ml-2 pl-12'}`}
              onClick={isCurrent ? undefined : () => navigate(`/m/gestion-de-casos/caso/${c.id}`)}>
              <div className="absolute left-2.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: color, top: '4px' }} />
              <div className={`p-4 rounded-lg border ${isCurrent ? 'bg-blue-50 border-blue-300 shadow-sm' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold" style={{ color: isCurrent ? '#1e40af' : '#1e293b' }}>{c.titulo || `Caso ${c.id.slice(0, 8)}`}</span>
                    {isCurrent && <Badge className="bg-blue-200 text-blue-800 text-[10px]">Actual</Badge>}
                    {c.cuenta_bloqueada && <Lock size={12} className="text-red-500" />}
                  </div>
                  <Badge className="text-[10px]" style={{ backgroundColor: `${color}20`, color, border: `1px solid ${color}40` }}>{c.estado}</Badge>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{c.created_at ? new Date(c.created_at).toLocaleDateString('es-AR') : '-'}</span>
                  <span>Score: <strong className={c.score_riesgo >= 80 ? 'text-red-600' : 'text-slate-700'}>{c.score_riesgo || 0}</strong></span>
                  <span>{c.total_alertas || 0} alertas</span>
                  {c.tipos_alerta?.slice(0, 3).map((t, j) => (
                    <Badge key={j} className="bg-slate-100 text-slate-600 text-[9px]">{t}</Badge>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClienteHistorial;
